// ─────────────────────────────────────────────────────────────
// Achievements — computed purely from existing profile data
// No backend changes needed. All inputs come from:
//   - user._count (discussions, followers, following)
//   - activityData (activity map, totalContributions)
//   - streak (computed inline in ProfilePage)
// ─────────────────────────────────────────────────────────────

export interface Achievement {
  id: string
  icon: string
  label: string
  description: string
  unlocked: boolean
  tier: 'bronze' | 'silver' | 'gold'
}

interface AchievementInput {
  discussions: number
  followers: number
  following: number
  totalContributions: number
  streak: number
  activity: Record<string, number>  // YYYY-MM-DD -> count
}

export function computeAchievements(input: AchievementInput): Achievement[] {
  const { discussions, followers, totalContributions, streak, activity } = input

  // Total active days (any day with contributions)
  const activeDays = Object.values(activity).filter(v => v > 0).length

  // Best single day
  const bestDay = Math.max(0, ...Object.values(activity))

  const all: Achievement[] = [
    // ── First steps ──────────────────────────────────────────
    {
      id: 'first_post',
      icon: '✍️',
      label: 'First Post',
      description: 'Posted your first discussion',
      unlocked: discussions >= 1,
      tier: 'bronze',
    },
    {
      id: 'conversationalist',
      icon: '💬',
      label: 'Conversationalist',
      description: 'Made 50 total contributions',
      unlocked: totalContributions >= 50,
      tier: 'bronze',
    },
    {
      id: 'regular',
      icon: '📅',
      label: 'Regular',
      description: 'Active on 10 different days',
      unlocked: activeDays >= 10,
      tier: 'bronze',
    },

    // ── Engagement ───────────────────────────────────────────
    {
      id: 'prolific',
      icon: '📝',
      label: 'Prolific',
      description: 'Posted 10 discussions',
      unlocked: discussions >= 10,
      tier: 'silver',
    },
    {
      id: 'century',
      icon: '💯',
      label: 'Century',
      description: 'Made 100 total contributions',
      unlocked: totalContributions >= 100,
      tier: 'silver',
    },
    {
      id: 'power_day',
      icon: '⚡',
      label: 'Power Day',
      description: '10+ contributions in a single day',
      unlocked: bestDay >= 10,
      tier: 'silver',
    },

    // ── Streaks ──────────────────────────────────────────────
    {
      id: 'streak_week',
      icon: '🔥',
      label: 'Week Streak',
      description: '7 day contribution streak',
      unlocked: streak >= 7,
      tier: 'silver',
    },
    {
      id: 'streak_fortnight',
      icon: '🔥🔥',
      label: 'On Fire',
      description: '14 day contribution streak',
      unlocked: streak >= 14,
      tier: 'gold',
    },
    {
      id: 'streak_month',
      icon: '🔥🔥🔥',
      label: 'Unstoppable',
      description: '30 day contribution streak',
      unlocked: streak >= 30,
      tier: 'gold',
    },

    // ── Social ───────────────────────────────────────────────
    {
      id: 'first_follower',
      icon: '👥',
      label: 'First Follower',
      description: 'Someone followed you',
      unlocked: followers >= 1,
      tier: 'bronze',
    },
    {
      id: 'rising_star',
      icon: '⭐',
      label: 'Rising Star',
      description: '5 followers',
      unlocked: followers >= 5,
      tier: 'silver',
    },
    {
      id: 'influencer',
      icon: '🌟',
      label: 'Influencer',
      description: '25 followers',
      unlocked: followers >= 25,
      tier: 'gold',
    },

    // ── Dedication ───────────────────────────────────────────
    {
      id: 'dedicated',
      icon: '🏅',
      label: 'Dedicated',
      description: 'Active on 30 different days',
      unlocked: activeDays >= 30,
      tier: 'gold',
    },
    {
      id: 'legend',
      icon: '🏆',
      label: 'Legend',
      description: '500 total contributions',
      unlocked: totalContributions >= 500,
      tier: 'gold',
    },
    {
      id: 'prolific_author',
      icon: '📚',
      label: 'Prolific Author',
      description: 'Posted 50 discussions',
      unlocked: discussions >= 50,
      tier: 'gold',
    },
  ]

  return all
}

export function computeStreak(activity: Record<string, number>): number {
  let streak = 0
  const todayUTC = new Date()
  todayUTC.setUTCHours(0, 0, 0, 0)
  const check = new Date(todayUTC)
  const todayKey = check.toISOString().slice(0, 10)
  if (!activity[todayKey]) check.setUTCDate(check.getUTCDate() - 1)
  while (true) {
    const key = check.toISOString().slice(0, 10)
    if (!activity[key]) break
    streak++
    check.setUTCDate(check.getUTCDate() - 1)
  }
  return streak
}
